import React, { useMemo } from 'react';

// Studio "Style de rénovation" — palette GLOBALE du projet (couleur murs, teinte
// sol, luminaires, ambiance). Les contrôles ne s'affichent que s'ils sont
// pertinents au regard des travaux réellement sélectionnés (croisement) : pas de
// peinture murs dans aucune pièce → pas de choix de couleur murs (et le rendu IA
// ne touchera pas aux murs).

export const WALL_COLORS = [
  { label: 'Blanc pur', hex: '#ffffff', prompt: 'clean matte pure white' },
  { label: 'Blanc cassé', hex: '#f3ede1', prompt: 'warm off-white (blanc cassé) matte paint' },
  { label: 'Greige', hex: '#cfc7b8', prompt: 'soft greige (warm grey-beige) matte paint' },
  { label: 'Gris perle', hex: '#c9ced4', prompt: 'light pearl grey matte paint' },
  { label: 'Bleu nuit', hex: '#2b3a55', prompt: 'one deep navy blue feature wall, the other walls matte white' },
  { label: 'Vert sauge', hex: '#9caf88', prompt: 'soft sage green matte paint' },
  { label: 'Terracotta', hex: '#c66a4e', prompt: 'one warm terracotta feature wall, the other walls off-white' },
];

export const FLOOR_TONES = [
  { label: 'Chêne clair', sw: '#d8b98a', prompt: 'light natural oak (PVC effet parquet)' },
  { label: 'Chêne moyen', sw: '#b07d4d', prompt: 'medium warm oak (PVC effet parquet)' },
  { label: 'Chêne foncé', sw: '#6e4a2f', prompt: 'dark walnut-toned wood (PVC effet parquet)' },
  { label: 'Carrelage gris clair', sw: '#cdd0d2', prompt: 'light grey large-format porcelain tiles' },
  { label: 'Carrelage anthracite', sw: '#4a4e54', prompt: 'anthracite large-format porcelain tiles' },
  { label: 'Béton ciré', sw: '#b8b4ad', prompt: 'light grey polished concrete (béton ciré)' },
];

export const LIGHTING = [
  { label: 'Spots encastrés', icon: '🔆', prompt: 'recessed LED spotlights in the ceiling' },
  { label: 'Suspensions design', icon: '💡', prompt: 'modern design pendant lights' },
  { label: 'Réglettes LED', icon: '➖', prompt: 'linear LED battens' },
  { label: 'Plafonniers LED', icon: '⬜', prompt: 'flush LED ceiling panels' },
];

export const AMBIANCES = [
  { label: 'Lumineux & épuré', prompt: 'bright, airy and minimalist' },
  { label: 'Chaleureux bois', prompt: 'warm and cozy with natural wood tones' },
  { label: 'Scandinave', prompt: 'Scandinavian style, light woods and soft neutral tones' },
  { label: 'Contemporain', prompt: 'contemporary, sleek and refined' },
  { label: 'Haussmannien', prompt: 'classic Parisian Haussmann elegance, refined mouldings' },
];

// Quelles surfaces sont travaillées dans AU MOINS une pièce ? (mêmes regex que le backend)
function deriveScope(rooms) {
  const keys = (rooms || []).flatMap((r) => (r.lines || []).map((l) => String(l.key || ''))).join(' ');
  return {
    walls: /murs\.peinture/.test(keys) || /preparation\./.test(keys),
    floor: /sols\./.test(keys),
    lighting: /electricite\.(spot|led|luminaire|panneau|plafonnier|point)/.test(keys),
  };
}

function Section({ title, hint, children }) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
      <div className="flex items-baseline gap-2 mb-3">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        {hint && <span className="text-xs text-slate-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export default function StyleStudio({ style = {}, onChange, rooms = [] }) {
  const scope = useMemo(() => deriveScope(rooms), [rooms]);
  const set = (patch) => onChange({ ...style, ...patch });
  const isSel = (a, b) => a && b && a.label === b.label;

  const noControls = !scope.walls && !scope.floor && !scope.lighting;

  return (
    <div className="space-y-3">
      {/* Ambiance — toujours */}
      <Section title="Ambiance générale" hint="— l'esprit du rendu">
        <div className="flex flex-wrap gap-2">
          {AMBIANCES.map((a) => (
            <button key={a.label} onClick={() => set({ ambiance: isSel(style.ambiance, a) ? null : a })}
              className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-colors min-h-[44px] ${
                isSel(style.ambiance, a) ? 'bg-orange-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}>
              {a.label}
            </button>
          ))}
        </div>
      </Section>

      {/* Couleur des murs — seulement si peinture murs quelque part */}
      {scope.walls && (
        <Section title="Couleur des murs" hint="— peinture prévue">
          <div className="flex flex-wrap gap-2">
            {WALL_COLORS.map((c) => (
              <button key={c.label} onClick={() => set({ wallColor: isSel(style.wallColor, c) ? null : c })}
                title={c.label}
                className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border-2 transition-colors min-h-[44px] ${
                  isSel(style.wallColor, c) ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                }`}>
                <span className="w-7 h-7 rounded-full border border-slate-300 shrink-0" style={{ background: c.hex }} />
                <span className="text-xs font-semibold text-slate-700">{c.label}</span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Teinte du sol — seulement si travaux de sol */}
      {scope.floor && (
        <Section title="Teinte du sol" hint="— revêtement prévu">
          <div className="flex flex-wrap gap-2">
            {FLOOR_TONES.map((f) => (
              <button key={f.label} onClick={() => set({ floorTone: isSel(style.floorTone, f) ? null : f })}
                className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border-2 transition-colors min-h-[44px] ${
                  isSel(style.floorTone, f) ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                }`}>
                <span className="w-7 h-7 rounded-md border border-slate-300 shrink-0" style={{ background: f.sw }} />
                <span className="text-xs font-semibold text-slate-700">{f.label}</span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Luminaires — seulement si électricité éclairage */}
      {scope.lighting && (
        <Section title="Luminaires" hint="— éclairage prévu">
          <div className="flex flex-wrap gap-2">
            {LIGHTING.map((l) => (
              <button key={l.label} onClick={() => set({ lighting: isSel(style.lighting, l) ? null : l })}
                className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-colors min-h-[44px] ${
                  isSel(style.lighting, l) ? 'bg-orange-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}>
                <span className="mr-1">{l.icon}</span>{l.label}
              </button>
            ))}
          </div>
        </Section>
      )}

      {noControls && (
        <p className="text-xs text-slate-400 px-1">
          Ajoute des travaux (peinture, sol, éclairage) dans l'étape précédente pour débloquer les options de style correspondantes.
          L'ambiance choisie ci-dessus s'appliquera quand même au rendu.
        </p>
      )}
    </div>
  );
}
